import { BulkReplacementRadio } from '../components/BulkReplacementModeChooser/BulkReplacementModeChooser';

import { AudienceTargetingsValue } from './audienceTargetings';
import { Region } from './common';
import { NumberRangeInputValues, PageStatus, RegionId, ValidationErrorData, ValidationErrorsData } from './common';
import { DisplayPageBlock } from './displayMap';
import { BannerPreviewData, CampaignType, GroupType } from './domain';
import { FeedCategoryNode } from './feedCategories';
import {
    GdBidModifierType,
    GdBusinessType,
    GdCampaignPlatform,
    GdFeedType,
    GdShowConditionAutobudgetPriority,
    GdSmartFilterConditionOperator,
    GdSmartFilterTab,
    GdSmartFilterTargetFunnel,
    GdStrategyType,
    GdUpdateStatus,
    GdViewType,
    GroupsQueryForEdit_client_feeds_rowset_categories
} from './graphql-grid';
import { GroupId } from './groups';
import {
    AdditionalTargetingField,
    RestInternalGroupField,
    TargetingBrowserEngines,
    TargetingBrowserNames,
    TargetingClids,
    TargetingClidTypes,
    TargetingDesktopInstalledApps,
    TargetingDeviceNames,
    TargetingDeviceVendors,
    TargetingInterfaceLangs,
    TargetingInternalNetwork,
    TargetingIsBrowser,
    TargetingIsMobile,
    TargetingIsTablet,
    TargetingIsTouch,
    TargetingIsVirused,
    TargetingIsYandexPlus,
    TargetingMobileInstalledApps,
    TargetingOsFamilies,
    TargetingOsNames,
    TargetingQueryOptions,
    TargetingQueryReferers,
    TargetingShowDates,
    TargetingTestIds,
    TargetingUserAgents,
    TargetingYandexUids,
    TargetingYsCookies
} from './internalGroups';
import { KeywordsData } from './keywordsEditorData';
import { MinusPhrasesShortLibraryItem } from './minusPhraseLibrary';
import { PITagsEditorState } from './piTags';
import { PricePackagesForGroup } from './pricePackages';
import {
    RateCorrection,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsValue
} from './ratesCorrections';
import { EditPageRecommendationsData } from './recommendations';
import { RetargetingConditionData } from './retargeting';
import { TargetPlatformTags } from './targetTagsEditor';
import { UserProfileRetargetingCondition } from './userProfileRetargeting';
import { Omit } from './utils';

export interface EditGroupsState {
    pageStatus: PageStatus;
    groupsIds: GroupId[];
    campaignsIds: Array<string>;
    isNew: boolean;
    isNewEmptyGroup: boolean; // пустая только что созданная группа - после ее сохранения переходим на создание баннеров
    errors: (string | ValidationErrorData)[];
    groups: Record<GroupId, EditGroupData>;
    recommendations: Record<GroupId, EditPageRecommendationsData>;
    feedsIds: string[];
    feeds: Feeds;
    groupFilterInspector: GroupFilterInspectorState;
    isPerformanceFeedModalOpen: boolean;
    feedsPopupFetchFirstRun: boolean;
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>;
    retargetingConditions?: Array<RetargetingConditionData>;
    rateCorrectionsConstants: RatesCorrectionsConstantsData;
    keywordsCountLimit: number;
    reqId?: string;
    productsList?: ProductsList;
}

export enum MassActionId {
    groupsEdit = 'groupsEdit'
}

export type GroupsEditMassActionsState = Record<MassActionId, GroupsEditMassActions>;

export interface GroupsEditMassActions {
    visibleModalActions: VisibleGroupsModalActions;
    data: GroupsEditMassActionsData;
}

export interface GroupsEditMassActionsData {
    minusPhrasesRadio: BulkReplacementRadio;
}

export interface VisibleGroupsModalActions {
    minusPhraseModal: boolean;
}

export type FeedCategory = Omit<GroupsQueryForEdit_client_feeds_rowset_categories, '__typename'>;

export interface Feed {
    id: string;
    name: string;
    updateStatus: GdUpdateStatus;
    businessType: GdBusinessType;
    categories: Array<FeedCategory>;
    feedType?: GdFeedType;
}

export interface Feeds {
    [key: string]: Feed;
}

export interface EditGroupAutotargeting {
    id?: string;
    isActive: boolean;
}

export type RegionSelectedMap = Record<RegionId, boolean>;

export interface EditGroupRegions {
    // имеется договоренность с бекендом о том что будем отправлять не только regionIds, но и selectedMap
    selectedMap: RegionSelectedMap;
    regionIds: RegionId[];
}

export interface FiltersEditor {
    isOpen: boolean;
    isLoading: boolean;
    auxiliaryFilter: string;
}

export interface RetargetingValue {
    id: string;
    retargetingId?: string;
}

export interface ShowConditionsRecommendationDataState {
    wasClosed: boolean;
}

export interface EditBaseGroupDataValue {
    name: string;
    campaignType: undefined;
    groupType: GroupType;
    piTagsEditor?: PITagsEditorState;
}

export interface EditContentPromotionGroupDataValue {
    name: string;
    campaignType: CampaignType.CONTENT_PROMOTION;
    groupType: GroupType;
    autotargeting: EditGroupAutotargeting;
    regions: EditGroupRegions;
    rateCorrections: RatesCorrectionsValue;
    keywords?: KeywordsData[];
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    minusKeywords: string[];
    piTagsEditor?: PITagsEditorState;
    generalRate?: number;
    availableGroups: GroupType[];
}

export interface EditTextGroupDataValue {
    name: string;
    campaignType: CampaignType.TEXT;
    groupType: GroupType;
    autotargeting: EditGroupAutotargeting;
    retargeting: Array<RetargetingValue>;
    keywords: KeywordsData[];
    minusKeywords: string[];
    rateCorrections: RatesCorrectionsValue;
    regions: EditGroupRegions;
    generalRate?: number;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    audienceTargetings: AudienceTargetingsValue;
    audienceTargetingsTemplateName: string;
    piTagsEditor?: PITagsEditorState;
}

export interface EditMcBannerGroupDataValue {
    name: string;
    campaignType: CampaignType.MCBANNER;
    groupType: GroupType;
    keywords: KeywordsData[];
    minusKeywords: string[];
    rateCorrections: RatesCorrectionsValue;
    regions: EditGroupRegions;
    generalRate?: number;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    piTagsEditor?: PITagsEditorState;
}

export interface EditPerformanceGroupDataValue {
    name: string;
    campaignType: CampaignType.PERFORMANCE;
    groupType: GroupType;
    hrefParams: string;
    bannerHeadersFeedField: string;
    bannerTextFeedField: string;
    regions: EditGroupRegions;
    minusKeywords: string[];
    rateCorrections: Record<RateCorrectionType, RateCorrection>;
    feedsPopupIsOpen: boolean;
    searchFeedFieldValue: string;
    selectedFeedId: string;
    feedId: string;
    filters: Array<EditGroupFilterItem>;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    piTagsEditor?: PITagsEditorState;
}

export interface EditCpmPriceGroupDataValue {
    name: string;
    campaignType: CampaignType.CPM_PRICE;
    groupType: GroupType;
    regions: EditGroupRegions;
    audienceTargetings: AudienceTargetingsValue;
    audienceTargetingsTemplateName: string;
    productRestrictionId: string;
    piTagsEditor?: PITagsEditorState;
    behaviors: string[];
    viewTypes?: GdViewType[];
    pricePackage?: PricePackagesForGroup;
    isDefault?: boolean;
    defaultBehaviors: string[];
}

export interface EditInternalGroupDataValue {
    [RestInternalGroupField.NAME]: string;
    [RestInternalGroupField.REGIONS]: EditGroupRegions;
    [RestInternalGroupField.CAMPAIGN_TYPE]:
        | CampaignType.INTERNAL_DISTRIB
        | CampaignType.INTERNAL_FREE
        | CampaignType.INTERNAL_AUTOBUDGET;
    [RestInternalGroupField.GROUP_TYPE]: GroupType;
    [AdditionalTargetingField.YANDEX_UIDS]?: TargetingYandexUids;
    [AdditionalTargetingField.INTERNAL_NETWORK]?: TargetingInternalNetwork;
    [AdditionalTargetingField.IS_BROWSER]?: TargetingIsBrowser;
    [AdditionalTargetingField.IS_TOUCH]?: TargetingIsTouch;
    [AdditionalTargetingField.IS_TABLET]?: TargetingIsTablet;
    [AdditionalTargetingField.IS_MOBILE]?: TargetingIsMobile;
    [AdditionalTargetingField.OS_NAMES]?: TargetingOsNames;
    [AdditionalTargetingField.OS_FAMILIES]?: TargetingOsFamilies;
    [AdditionalTargetingField.BROWSER_NAMES]?: TargetingBrowserNames;
    [AdditionalTargetingField.BROWSER_ENGINES]?: TargetingBrowserEngines;
    [AdditionalTargetingField.DEVICE_NAMES]?: TargetingDeviceNames;
    [AdditionalTargetingField.DEVICE_VENDORS]?: TargetingDeviceVendors;
    [AdditionalTargetingField.QUERY_REFERERS]?: TargetingQueryReferers;
    [AdditionalTargetingField.INTERFACE_LANGS]?: TargetingInterfaceLangs;
    [AdditionalTargetingField.USER_AGENTS]?: TargetingUserAgents;
    [AdditionalTargetingField.SHOW_DATES]?: TargetingShowDates;
    [AdditionalTargetingField.CLID_TYPES]?: TargetingClidTypes;
    [AdditionalTargetingField.CLIDS]?: TargetingClids;
    [AdditionalTargetingField.DESKTOP_INSTALLED_APPS]?: TargetingDesktopInstalledApps;
    [AdditionalTargetingField.MOBILE_INSTALLED_APPS]?: TargetingMobileInstalledApps;
    [AdditionalTargetingField.QUERY_OPTIONS]?: TargetingQueryOptions;
    [AdditionalTargetingField.TEST_IDS]?: TargetingTestIds;
    [AdditionalTargetingField.YS_COOKIES]?: TargetingYsCookies;
    [AdditionalTargetingField.IS_YANDEX_PLUS]?: TargetingIsYandexPlus;
    [AdditionalTargetingField.IS_VIRUSED]?: TargetingIsVirused;
    [RestInternalGroupField.START_TIME]?: GraphQlLocalDateTime;
    [RestInternalGroupField.FINISH_TIME]?: GraphQlLocalDateTime;
    [RestInternalGroupField.LEVEL]?: string;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    piTagsEditor?: PITagsEditorState;
}

export enum CpmGroupShowConditionType {
    USER_PROFILE = 'USER_PROFILE',
    KEYWORD = 'KEYWORD'
}

export interface EditCpmGroupDataValue {
    name: string;
    campaignType: CampaignType.CPM_BANNER;
    groupType: GroupType;
    autotargeting: EditGroupAutotargeting;
    retargetingCondition?: UserProfileRetargetingCondition;
    keywords?: KeywordsData[];
    showConditionType: CpmGroupShowConditionType;
    rateCorrections: RatesCorrectionsValue;
    rateCurrentType: RateCorrectionType;
    regions: EditGroupRegions;
    generalRate?: number;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    outdoorMap?: {
        pageBlocks: DisplayPageBlock[];
    };
    indoorMap?: {
        pageBlocks: DisplayPageBlock[];
    };
    audienceTargetings: AudienceTargetingsValue;
    audienceTargetingsTemplateName: string;
    minusKeywords?: string[];
    audienceCollection?: AudienceCollection[];
    productRestrictionId: string;
    piTagsEditor?: PITagsEditorState;
}

export type EditGroupDataValue =
    | EditTextGroupDataValue
    | EditPerformanceGroupDataValue
    | EditInternalGroupDataValue
    | EditCpmGroupDataValue
    | EditMcBannerGroupDataValue
    | EditContentPromotionGroupDataValue
    | EditCpmPriceGroupDataValue
    | EditBaseGroupDataValue;

export interface EditGroupData {
    id: GroupId;
    campaign: {
        name: string;
        id: string;
        strategyType: GdStrategyType;
        platform?: GdCampaignPlatform;
        strategy: {
            isAutoBudget: boolean;
        };
        startDate?: string;
        endDate?: string;
        pricePackageId?: string;
        flightOrderVolume?: string;
        regionTrimmedTree?: Region[];
        regionIds?: RegionId[];
    };
    serverValue: EditGroupDataValue;
    value: EditGroupDataValue;
    filtersEditor: FiltersEditor;
    hasGeneralRate: boolean;
    isEditable: boolean;
    isSaved: boolean;
    errors: ValidationErrorsData;
    bannersCount: number;
    isNew: boolean;
    showConditionsRecommendationData: ShowConditionsRecommendationDataState;
    previewBanners: PreviewBanners;
}

export interface PreviewBanners {
    isBannersPopupVisible: boolean;
    mainBanner?: BannerPreviewData;
}

export interface EditInternalGroupData extends EditGroupData {
    serverValue: EditInternalGroupDataValue;
    value: EditInternalGroupDataValue;
}

export enum AudienceCollection {
    COMPLETE = 'COMPLETE',
    FIRST_QUARTILE = 'FIRST_QUARTILE',
    MIDPOINT = 'MIDPOINT',
    START = 'START',
    THIRD_QUARTILE = 'THIRD_QUARTILE'
}

export interface AudienceCollectionValue {
    value: AudienceCollection;
    text: string;
}

export interface EditGroupSessionValue {
    groupsIds: Array<GroupId>;
    campaignsIds: Array<string>;
    isNew: boolean;
    isNewEmptyGroup: boolean;
}

export interface UpdateDataForEditGroups {
    groups: Record<string, EditGroupData>;
    retargetingConditions?: RetargetingConditionData[];
    reqId?: string;
}

export interface UpdateRetargetingConditionsForGroupsEdit {
    retargetingConditions: RetargetingConditionData[];
}

export interface DataForEditGroups {
    groups: Record<string, EditGroupData>;
    feedsIds: string[];
    feeds: Feeds;
    isLibraryListsFetched?: boolean;
    allowedRateCorrectionsData?: Record<GroupType, Array<GdBidModifierType>>;
    rateCorrectionsConstants?: RatesCorrectionsConstantsData;
    retargetingConditions?: RetargetingConditionData[];
    keywordsCountLimit: number;
    reqId?: string;
}

export interface DataForEditInternalGroups extends DataForEditGroups {
    groups: Record<string, EditInternalGroupData>;
}

export interface FeedFilterCampaign {
    strategy?: GdStrategyType;
    platform?: GdCampaignPlatform;
}

export interface EditGroupFilterItem {
    name: string;
    type: GdSmartFilterTargetFunnel;
    priceCpa?: number;
    priceCpc?: number;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    id: string;
    tab: GdSmartFilterTab;
    conditions: Array<FeedFilterCondition>;
    feedCategories: Array<FeedCategoryNode>;
    categoryIds: Array<string>;
    isAvailable: boolean;
    vendors: string;
    priceRange: NumberRangeInputValues;
    campaign: FeedFilterCampaign;
    errors?: ValidationErrorsData;
}

export interface FeedFilterCondition {
    field: string;
    operator: GdSmartFilterConditionOperator;
    arguments: FeedFilterOperationControlValues;
    id: string;
    errors?: Array<ValidationErrorData>;
}

export interface FeedFilterSchemaOperator {
    type: GdSmartFilterConditionOperator;
    maxItems: number;
}

export enum FeedFilterSchemaFieldType {
    NUMBER = 'NUMBER',
    STRING = 'STRING',
    ENUM = 'ENUM'
}

export interface FeedFilterNumberFieldParams {
    min: number;
    max: number;
    precision: number;
}

export interface FeedFilterStringFieldParams {
    minLength: number;
    maxLength: number;
    pattern?: string;
}

export interface FeedFilterEnumFieldParams {
    values: Array<string>;
}

interface FeedFilterSchemaFieldBase {
    name: string;
    fieldType: FeedFilterSchemaFieldType;
    operators: Array<FeedFilterSchemaOperator>;
}

export interface FeedFilterSchemaNumberField extends FeedFilterSchemaFieldBase, FeedFilterNumberFieldParams {
    fieldType: FeedFilterSchemaFieldType.NUMBER;
}

export interface FeedFilterSchemaStringField extends FeedFilterSchemaFieldBase, FeedFilterStringFieldParams {
    fieldType: FeedFilterSchemaFieldType.STRING;
}

export interface FeedFilterSchemaEnumField extends FeedFilterSchemaFieldBase, FeedFilterEnumFieldParams {
    fieldType: FeedFilterSchemaFieldType.ENUM;
}

export type FeedFilterSchemaField =
    | FeedFilterSchemaNumberField
    | FeedFilterSchemaStringField
    | FeedFilterSchemaEnumField;

export interface FeedFilterSchema {
    fields: Array<FeedFilterSchemaField>;
}

export type NumberCondition =
    | NumberGreaterThanCondition
    | NumberLessThanCondition
    | NumberRangeCondition
    | NumberEqualsAnyCondition
    | NumberExistsCondition;

export interface NumberGreaterThanCondition {
    operator: GdSmartFilterConditionOperator.GREATER_THAN;
    arguments: Array<number | undefined>;
}

export interface NumberLessThanCondition {
    operator: GdSmartFilterConditionOperator.LESS_THAN;
    arguments: Array<number | undefined>;
}

export interface NumberRangeCondition {
    operator: GdSmartFilterConditionOperator.RANGE;
    arguments: Array<NumberRangeInputValues>;
}

export interface NumberEqualsAnyCondition {
    operator: GdSmartFilterConditionOperator.EQUALS_ANY;
    arguments: Array<string>;
}

export interface NumberExistsCondition {
    operator: GdSmartFilterConditionOperator.EXISTS;
    arguments: Array<string>;
}

export type FeedFilterNumberValues = Array<string> | Array<number | undefined> | Array<NumberRangeInputValues>;

export type FeedFilterEnumValues = Array<string>;
export type FeedFilterStringValues = Array<string>;

export type FeedFilterOperationControlValues = FeedFilterEnumValues | FeedFilterNumberValues | FeedFilterStringValues;

export interface GroupFilterInspectorState {
    adGroupId?: GroupId;
    filterId?: string;
    feedId?: string;
    filter: EditGroupFilterItem;
    isOpen: boolean;
    inspectorStatus: PageStatus;
    schema: FeedFilterSchema;
}

export type ProductsList = Record<string, ProductsListItem>;

export interface ProductsListItem {
    id: string;
    type: CampaignType;
    restrictions: ProductsListItemRestriction[];
}

export interface ProductsListItemRestriction {
    id: string;
    groupType: GroupType;
    active: boolean;
    showConditionType: CpmGroupShowConditionType;
    conditions: ProductsListItemRestrictionCondition[];
}

export type ProductsRestrictionNestedConditions =
    | ConditionsCorrectionsKeys
    | ConditionsSocialDemoKeys
    | TargetPlatformTags;

export interface ProductsListItemRestrictionCondition {
    name: ConditionsKeys;
    nestedConditions?: ProductsRestrictionNestedConditions[];
}

export interface ProductsRestrictionGroupTypeItem {
    groupType: GroupType;
    restrictionId: string;
}

export enum ConditionsKeys {
    geo = 'geo',
    phrases = 'phrases',
    social_demo = 'social_demo',
    correction = 'correction',
    goal_stat = 'goal_stat',
    audio_genres = 'audio_genres',
    advert_pages_outdoor = 'advert_pages_outdoor',
    advert_pages_indoor = 'advert_pages_indoor',
    bs_target_tags = 'bs_target_tags',
    campaign_options = 'campaign_options',
    geo_short = 'geo_short',
    view_types = 'view_types',
    additional_targeting = 'additional_targeting'
}

export enum ConditionsCorrectionsKeys {
    device = 'device',
    weather = 'weather',
    socdem = 'socdem',
    target_audience = 'target_audience',
    traffic = 'traffic'
}

export enum ConditionsSocialDemoKeys {
    social_demo = 'social_demo',
    family = 'family',
    behaviors = 'behaviors',
    interests = 'interests',
    audience = 'audience'
}

export enum ConditionsCorrectionsApiKeys {
    correction_device = 'correction_device',
    correction_weather = 'correction_weather',
    correction_socdem = 'correction_socdem',
    correction_target_audience = 'correction_target_audience',
    correction_traffic = 'correction_traffic'
}

export function getEditGroupDataValueWithMinusPhrases(
    value: EditGroupDataValue
):
    | EditTextGroupDataValue
    | EditPerformanceGroupDataValue
    | EditMcBannerGroupDataValue
    | EditContentPromotionGroupDataValue
    | undefined {
    switch (value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
        case CampaignType.MCBANNER:
        case CampaignType.CONTENT_PROMOTION:
            return value;
        default:
            return;
    }
}

type EditGroupDataValueWithKeywords =
    | EditTextGroupDataValue
    | EditMcBannerGroupDataValue
    | EditCpmGroupDataValue
    | EditContentPromotionGroupDataValue;

export function getEditGroupDataValueWithKeywords(
    value: EditGroupDataValue
): EditGroupDataValueWithKeywords | undefined {
    switch (value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.MCBANNER:
        case CampaignType.CPM_BANNER:
        case CampaignType.CONTENT_PROMOTION:
            return value;
        default:
            return;
    }
}

type EditGroupDataValueWithRegions =
    | EditTextGroupDataValue
    | EditPerformanceGroupDataValue
    | EditMcBannerGroupDataValue
    | EditCpmGroupDataValue
    | EditInternalGroupDataValue
    | EditContentPromotionGroupDataValue;

export function getEditGroupDataValueWithRegions(value: EditGroupDataValue): EditGroupDataValueWithRegions | undefined {
    switch (value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
        case CampaignType.MCBANNER:
        case CampaignType.CPM_BANNER:
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_AUTOBUDGET:
        case CampaignType.CONTENT_PROMOTION:
            return value;
        default:
            return;
    }
}

export function groupHasPreviewBanners(groupType: GroupType): boolean {
    switch (groupType) {
        case GroupType.TEXT:
        case GroupType.CPM_BANNER:
        case GroupType.CPM_VIDEO:
        case GroupType.MOBILE_CONTENT:
        case GroupType.PERFORMANCE:
        case GroupType.MCBANNER:
        case GroupType.CONTENT_PROMOTION_VIDEO:
        case GroupType.CONTENT_PROMOTION_COLLECTION:
        case GroupType.CPM_INDOOR:
        case GroupType.CPM_OUTDOOR:
        case GroupType.CPM_AUDIO:
        case GroupType.CPM_GEOPRODUCT:
        case GroupType.CPM_YNDX_FRONTPAGE:
        case GroupType.CPM_PRICE:
            return true;
        default:
            return false;
    }
}
